import React from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { IshikawaCause, IshikawaCauseCategory } from '../../types/rca.types';

interface IshikawaCategoryCardProps {
    cause: IshikawaCause;
    isExpanded: boolean;
    toggleCategory: (id: string) => void;
    updatePrimaryCause: (id: string, value: string) => void;
    addSecondaryCause: (id: string) => void;
    updateSecondaryCause: (id: string, index: number, value: string) => void;
    removeSecondaryCause: (id: string, index: number) => void;
    togglePotentialRoot: (id: string) => void;
    getCategoryColor: (category: IshikawaCauseCategory) => string;
}

export const IshikawaCategoryCard: React.FC<IshikawaCategoryCardProps> = ({
    cause,
    isExpanded,
    toggleCategory,
    updatePrimaryCause,
    addSecondaryCause,
    updateSecondaryCause,
    removeSecondaryCause,
    togglePotentialRoot,
    getCategoryColor
}) => {
    return (
        <div className={`border-2 rounded-lg p-4 ${getCategoryColor(cause.category)}`}>
            <button
                onClick={() => toggleCategory(cause.id)}
                className="w-full flex items-center justify-between mb-3"
            >
                <h3 className="text-lg font-semibold">{cause.category}</h3>
                {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>

            {isExpanded && (
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium mb-1">Primary Cause</label>
                        <textarea
                            value={cause.primaryCause}
                            onChange={(e) => updatePrimaryCause(cause.id, e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder={`What ${cause.category.toLowerCase()} factor could contribute?`}
                        />
                    </div>

                    {cause.secondaryCauses.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Sub-Causes</label>
                            <div className="space-y-2">
                                {cause.secondaryCauses.map((secondary, index) => (
                                    <div key={index} className="flex gap-2">
                                        <input
                                            type="text"
                                            value={secondary}
                                            onChange={(e) => updateSecondaryCause(cause.id, index, e.target.value)}
                                            className="flex-1 px-3 py-1 border border-gray-300 rounded bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="More specific cause..."
                                        />
                                        <button
                                            onClick={() => removeSecondaryCause(cause.id, index)}
                                            className="px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                        <button
                            onClick={() => addSecondaryCause(cause.id)}
                            className="text-sm flex items-center gap-1 px-2 py-1 hover:bg-white/50 rounded transition-colors"
                        >
                            <Plus className="w-3 h-3" />
                            Add Sub-Cause
                        </button>

                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={cause.isPotentialRoot}
                                onChange={() => togglePotentialRoot(cause.id)}
                                className="rounded"
                            />
                            Potential Root
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};
